import { CricketDetails, FootballDetails } from '@/lib/types/enrollment';
import { API_ENDPOINTS } from './endpoints';
import { fetchApi } from '@/lib/utils/api';

interface UserResponse {
  id: string;
  email: string;
  name: string | null;
  image?: string | null;
  role: 'admin' | 'guest' | 'default';
  sportDetails: {
    [key: string]: CricketDetails | FootballDetails;
  };
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say';
  countryCode?: string;
  mobileNumber?: string;
}

interface LoginInput {
  email: string;
  password: string;
}

interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  profilePicture?: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say';
  countryCode?: string;
  mobileNumber?: string;
}

interface ForgotPasswordInput {
  email: string;
}

interface ResetPasswordInput {
  token: string;
  password: string;
}

interface GoogleLoginInput {
  code: string;
}

export const authService = {
  async login(data: LoginInput): Promise<{ token: string }> {
    const response = await fetchApi<{ token: string }>(
      API_ENDPOINTS.AUTH.LOGIN,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: false,
      }
    );
    return response.result;
  },

  async logout(): Promise<void> {
    // Logout endpoint call if needed
    // For now, just a placeholder as logout is handled client-side
  },

  async loginWithGoogle(data: GoogleLoginInput): Promise<{ token: string }> {
    const response = await fetchApi<{ token: string }>(
      API_ENDPOINTS.AUTH.GOOGLE_CODE_SIGNIN,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: false,
      }
    );
    return response.result;
  },

  async register(data: RegisterInput): Promise<{ token: string }> {
    const response = await fetchApi<{ token: string }>(
      API_ENDPOINTS.AUTH.REGISTER,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: false,
      }
    );
    return response.result;
  },

  async getProfile(): Promise<UserResponse> {
    const response = await fetchApi<UserResponse>(API_ENDPOINTS.AUTH.ME);
    return response.result;
  },

  async updateProfile(
    userId: string,
    data: UpdateProfileInput
  ): Promise<UserResponse> {
    const response = await fetchApi<UserResponse>(
      API_ENDPOINTS.USERS.UPDATE_PROFILE(userId),
      {
        method: 'PATCH',
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          profilePicture: data.profilePicture,
          dateOfBirth: data.dateOfBirth,
          gender: data.gender,
          countryCode: data.countryCode,
          mobileNumber: data.mobileNumber,
        }),
      }
    );
    return response.result;
  },

  async updatePassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    await fetchApi<void>(API_ENDPOINTS.USERS.UPDATE_PASSWORD(userId), {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  async verifyEmail(token: string): Promise<void> {
    await fetchApi<void>(API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
      method: 'POST',
      body: JSON.stringify({ token }),
      requireAuth: false,
    });
  },

  async forgotPassword(data: ForgotPasswordInput): Promise<void> {
    await fetchApi<void>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: false,
    });
  },

  async resetPassword(data: ResetPasswordInput): Promise<void> {
    await fetchApi<void>(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: false,
    });
  },
};
